import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { formatDateToFrCh } from 'src/app/_helpers/utils';
import { DocumentService } from 'src/app/_services/document.service';
import { InsuranceFlowProposalSnapshotResponse } from 'src/app/_types/insurance-flow.types';
import { ProductDetails, ProposalDetails } from 'src/app/_types/proposals.type';
import {
  getPeriodicityLabel,
  Periodicity,
  ProposalState,
} from 'src/app/enums/proposal.enums';
import { PdfViewerModalComponent } from 'src/app/shared/components/pdf-viewer-modal/pdf-viewer-modal.component';
import { ProposalProductsDisplayerComponent } from 'src/app/shared/components/view-proposals/proposal-products-displayer/proposal-products-displayer.component';
import { CardComponent } from 'src/app/shared/ui-kit/card/card.component';

type ProposalSnapshotLike =
  | InsuranceFlowProposalSnapshotResponse
  | ProposalDetails;

type NormalizedProposalValues = {
  startDate?: string;
  endDate?: string;
  offerSignatureDate?: string;
  periodicity?: string;
  coversPeriodicity?: string;
  category?: string;
  subcategory?: string;
  productItems?: ProductDetails[];
  premiumAmount?: string;
  totalPremiumAmount?: string;
  keyFeatures?: string;
  keyTags?: string;
  productBenefits?: string;
  policyDocItems?: string[];
  offerId?: string;
  contractDocItems?: string[];
  policyId?: string;
};

/**
 * What tells two document entries apart. A stored file is its key whatever
 * form it arrives in — a bare key from the proposal, or a full storage URL in
 * an older snapshot, signed or not — so its path tail without the query. An
 * insurer's link is kept whole.
 */
const documentIdentity = (entry: string): string => {
  const path = entry.split('?')[0];
  if (
    /^https?:\/\//i.test(path) &&
    !path.toLowerCase().includes('amazonaws.com')
  ) {
    return entry;
  }
  return path.substring(path.lastIndexOf('/') + 1);
};

@Component({
  selector: 'app-task-log-proposal-fields-diff',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    PdfViewerModalComponent,
    ProposalProductsDisplayerComponent,
  ],
  templateUrl: './task-log-proposal-fields-diff.component.html',
})
export class TaskLogProposalFieldsDiffComponent {
  @Input() currentSnapshot: ProposalSnapshotLike | null = null;

  @Input() baselineSnapshot: ProposalSnapshotLike | null = null;

  @Input() compare = true;

  @ViewChild('pdfViewer') viewer?: PdfViewerModalComponent;

  private isDocumentModalOpenOrLoading = false;

  constructor(private readonly documentService: DocumentService) {}

  protected get isPolicyCreated(): boolean {
    return this.currentSnapshot?.proposalState === ProposalState.POLICY_CREATED;
  }

  protected get currentData(): NormalizedProposalValues {
    return this.buildNormalizedValues(this.currentSnapshot);
  }

  protected get baselineData(): NormalizedProposalValues {
    return this.buildNormalizedValues(this.baselineSnapshot);
  }

  protected get hasVisibleFields(): boolean {
    if (!this.currentSnapshot) {
      return false;
    }

    const current = this.currentData;
    const baseline = this.baselineData;
    const sharedFields = [
      current.startDate,
      baseline.startDate,
      current.endDate,
      baseline.endDate,
      current.offerSignatureDate,
      baseline.offerSignatureDate,
      current.periodicity,
      baseline.periodicity,
      current.coversPeriodicity,
      baseline.coversPeriodicity,
      current.category,
      baseline.category,
      current.subcategory,
      baseline.subcategory,
      current.premiumAmount,
      baseline.premiumAmount,
      current.totalPremiumAmount,
      baseline.totalPremiumAmount,
    ];

    if (
      sharedFields.some((value) => this.hasValue(value)) ||
      (current.productItems?.length ?? 0) > 0 ||
      (baseline.productItems?.length ?? 0) > 0 ||
      // A policy document may be filed on an accepted offer too, before the policy exists.
      (current.contractDocItems?.length ?? 0) > 0 ||
      (baseline.contractDocItems?.length ?? 0) > 0
    ) {
      return true;
    }

    if (this.isPolicyCreated) {
      return (
        this.hasValue(current.policyId) || this.hasValue(baseline.policyId)
      );
    }

    return (
      this.hasValue(current.keyFeatures) ||
      this.hasValue(baseline.keyFeatures) ||
      this.hasValue(current.keyTags) ||
      this.hasValue(baseline.keyTags) ||
      this.hasValue(current.productBenefits) ||
      this.hasValue(baseline.productBenefits) ||
      this.hasValue(current.offerId) ||
      this.hasValue(baseline.offerId) ||
      (current.policyDocItems?.length ?? 0) > 0 ||
      (baseline.policyDocItems?.length ?? 0) > 0
    );
  }

  protected isChanged(currentValue?: string, baselineValue?: string): boolean {
    return this.compare && (currentValue || '-') !== (baselineValue || '-');
  }

  protected getPeriodicityDisplayLabel(periodicity?: string): string {
    if (!periodicity) {
      return '-';
    }

    return getPeriodicityLabel(periodicity as Periodicity);
  }

  /**
   * Compared as sets of {@link documentIdentity}: the backend orders linked
   * documents before legacy ones, so an older snapshot may list the same files
   * in another order, or as URLs rather than keys — or as both. Every upload
   * gets a fresh key, so collapsing duplicates hides no real change.
   */
  protected areDocumentListsChanged(
    currentValue?: string[],
    baselineValue?: string[]
  ): boolean {
    if (!this.compare) {
      return false;
    }

    const identities = (documents?: string[]): string =>
      JSON.stringify(
        [...new Set((documents ?? []).map(documentIdentity))].sort()
      );
    return identities(currentValue) !== identities(baselineValue);
  }

  protected openDocument(key: string): void {
    if (!key || !this.viewer || this.isDocumentModalOpenOrLoading) {
      return;
    }

    const { viewer } = this;
    this.isDocumentModalOpenOrLoading = true;
    const openViewerWithUrl = (url: string): void => {
      try {
        viewer.pdfFileUrl = url;
        viewer.openModal();
        if (viewer.modalRef) {
          viewer.modalRef.result.finally(() => {
            this.isDocumentModalOpenOrLoading = false;
          });
          return;
        }
      } catch {
        // If opening the modal fails unexpectedly, release the click lock.
      }

      this.isDocumentModalOpenOrLoading = false;
    };

    this.documentService.getPresignedUrl(key).subscribe({
      next: (response) => {
        openViewerWithUrl(response.data.preSignedUrl);
      },
      error: () => {
        // Keep backward compatibility when source is already a direct URL.
        openViewerWithUrl(key);
      },
    });
  }

  private buildNormalizedValues(
    snapshot: ProposalSnapshotLike | null
  ): NormalizedProposalValues {
    if (!snapshot) {
      return {};
    }

    const proposalDetails = 'policyDetails' in snapshot ? snapshot : undefined;
    const policyDetails = proposalDetails?.policyDetails;

    const source = {
      startDate:
        policyDetails?.startDate ??
        ('startDate' in snapshot ? snapshot.startDate : undefined),
      endDate:
        policyDetails?.endDate ??
        ('endDate' in snapshot ? snapshot.endDate : undefined),
      offerSignatureDate: snapshot.offerSignatureDate,
      periodicity:
        policyDetails?.periodicity ??
        ('periodicity' in snapshot ? snapshot.periodicity : undefined),
      coversPeriodicity:
        policyDetails?.coversPeriodicity ??
        ('coversPeriodicity' in snapshot
          ? snapshot.coversPeriodicity
          : undefined),
      categoryDetails:
        policyDetails?.categoryDetails ??
        ('categoryDetails' in snapshot ? snapshot.categoryDetails : undefined),
      productItems:
        policyDetails?.productDetails?.productList ??
        this.extractProducts(
          'productDetails' in snapshot ? snapshot.productDetails : undefined
        ),
      premiumAmount:
        policyDetails?.premiumAmount ??
        ('premiumAmount' in snapshot ? snapshot.premiumAmount : undefined),
      totalAmount:
        policyDetails?.totalAmount ??
        ('totalAmount' in snapshot ? snapshot.totalAmount : undefined),
      keyFeatures:
        policyDetails?.keyFeatures ??
        ('keyFeatures' in snapshot ? snapshot.keyFeatures : undefined),
      keyTags:
        policyDetails?.keyTags ??
        ('keyTags' in snapshot ? snapshot.keyTags : undefined),
      productBenefits:
        policyDetails?.productBenefits ??
        ('productBenefits' in snapshot ? snapshot.productBenefits : undefined),
      policyDocs:
        policyDetails?.policyDocs ??
        ('policyDocs' in snapshot ? snapshot.policyDocs : undefined),
      offerId:
        policyDetails?.offerId ??
        ('offerId' in snapshot ? snapshot.offerId : undefined),
      policyId:
        policyDetails?.policyId ??
        ('policyId' in snapshot ? snapshot.policyId : undefined),
    };

    return {
      startDate: this.formatDate(source.startDate),
      endDate: this.formatDate(source.endDate),
      offerSignatureDate: this.formatDate(source.offerSignatureDate),
      periodicity: source.periodicity,
      coversPeriodicity: source.coversPeriodicity ?? undefined,
      category:
        source.categoryDetails?.categoryNameInFrench ??
        source.categoryDetails?.categoryName,
      subcategory:
        source.categoryDetails?.subCategoryNameInFrench ??
        source.categoryDetails?.subCategoryName,
      productItems: source.productItems,
      premiumAmount: this.formatAmount(source.premiumAmount),
      totalPremiumAmount: this.formatAmount(source.totalAmount),
      keyFeatures: source.keyFeatures,
      keyTags: this.formatKeyTags(source.keyTags),
      productBenefits: source.productBenefits,
      policyDocItems: this.normalizeDocumentEntries(source.policyDocs),
      offerId: source.offerId,
      contractDocItems: this.normalizeDocumentEntries(snapshot.contractDoc),
      policyId: source.policyId,
    };
  }

  private formatDate(value?: string | Date | null): string | undefined {
    return formatDateToFrCh(value);
  }

  private formatKeyTags(value?: unknown): string | undefined {
    const tags = this.extractKeyTagLabels(value);
    return tags.length > 0 ? tags.join(', ') : undefined;
  }

  private extractKeyTagLabels(value?: unknown): string[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value
        .map((tag) => this.extractKeyTagLabel(tag))
        .filter((tag) => tag.trim().length > 0);
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return [];
      }

      if (trimmedValue.startsWith('[')) {
        try {
          const parsedValue = JSON.parse(trimmedValue);
          if (Array.isArray(parsedValue)) {
            return this.extractKeyTagLabels(parsedValue);
          }
        } catch {
          // Keep the raw string when JSON parsing fails.
        }
      }

      return [trimmedValue];
    }

    const label = this.extractKeyTagLabel(value);
    return label.trim().length > 0 ? [label] : [];
  }

  private extractKeyTagLabel(tag: unknown): string {
    if (typeof tag === 'string') {
      return tag;
    }

    if (tag && typeof tag === 'object' && 'display' in tag) {
      const { display } = tag as { display?: unknown };
      return typeof display === 'string' ? display : '';
    }

    if (typeof tag === 'number' || typeof tag === 'boolean') {
      return String(tag);
    }

    return '';
  }

  private formatAmount(value?: number): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    return `${value} CHF`;
  }

  private normalizeDocumentEntries(value?: string[] | string): string[] {
    let rawValues: string[] = [];
    if (Array.isArray(value)) {
      rawValues = value;
    } else if (value) {
      rawValues = [value];
    }

    const normalized: string[] = [];

    rawValues.forEach((rawValue) => {
      if (!rawValue) {
        return;
      }

      const parsedArray = this.tryParseJsonArray(rawValue);
      if (parsedArray) {
        parsedArray.forEach((item) => {
          const cleanedItem = item.trim();
          if (cleanedItem.length > 0) {
            normalized.push(cleanedItem);
          }
        });
        return;
      }

      const cleanedValue = rawValue.trim();
      if (cleanedValue.length > 0) {
        normalized.push(cleanedValue);
      }
    });

    return normalized;
  }

  private hasValue(value?: string): boolean {
    return value !== undefined && value !== null && value.trim().length > 0;
  }

  private extractProducts(value: unknown): ProductDetails[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.filter(
        (item): item is ProductDetails => !!item && typeof item === 'object'
      );
    }

    if (value && typeof value === 'object') {
      const objectValue = value as { productList?: unknown };
      if (Array.isArray(objectValue.productList)) {
        return objectValue.productList.filter(
          (item): item is ProductDetails => !!item && typeof item === 'object'
        );
      }

      return [value as ProductDetails];
    }

    return [];
  }

  private tryParseJsonArray(value: string): string[] | null {
    const trimmedValue = value.trim();
    if (!trimmedValue.startsWith('[') || !trimmedValue.endsWith(']')) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(trimmedValue);
      if (!Array.isArray(parsedValue)) {
        return null;
      }

      return parsedValue.filter(
        (item): item is string =>
          typeof item === 'string' && item.trim().length > 0
      );
    } catch {
      return null;
    }
  }
}
